import { BlobServiceClient } from "@azure/storage-blob";
import { computed, effect, signal } from "@preact/signals-react";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import {
  entryValues,
  formErrors,
  galleryValues,
  selectedStep,
  selectedTab,
  tableValues,
  template,
} from "../../Entry/Manage/EditEntrySignals";
import { selectedTemplate } from "../../Entry/View/ViewEntrySignals";
import { selectedHamburgerIndex } from "../../Navigation/HamburgerMenu/HamburgerMenuSignals";
import { userHasAccessToResource } from "../../Utilities/AuthorizationUtil";
import { saveUserPreferencesDebounced } from "../Services/UserPreferencesService";

export const userEmail = signal();
export const allTemplates = signal([]);
export const allDatasets = signal([]);
export const userEntries = signal();
export const entriesLoaded = signal(false);
export const msalInstance = signal();
export const envVariables = signal();
export const isAdmin = signal(false);
export const allRoles = signal([]);
export const selectedRole = signal();
export const signalRIssuerId = signal(uuidv4());
export const viewEntryHasColumnWidth = signal(false);

export const userData = signal(getUserDataFromLocalStorage());
function getUserDataFromLocalStorage() {
  const userDataString = localStorage.getItem("userData");
  if (userDataString) {
    return JSON.parse(userDataString);
  }
  return {
    userEmail: "",
    groups: [],
    officeLocation: "",
    roles: [],
    permissions: [],
  };
}
effect(() => {
  localStorage.setItem("userData", JSON.stringify(userData.value));
});

export const isAuthenticated = signal(false);
export const notification = signal({
  show: false,
  type: "",
  message: "",
  error: { message: "", stack: "" },
});

export const advancedOptionsEnabled = computed(() => {
  const advancedAccessList = allDatasets.value?.find(
    (dataset) => dataset.id === "1955254f-8939-4aba-a444-b0c6f0cbe9f0"
  );
  return advancedAccessList?.values.some(
    (value) => value.value.toLowerCase() === userEmail.value?.toLowerCase()
  );
});

export const userTemplates = computed(() => {
  return allTemplates.value?.filter((template) => {
    if (isAdmin.value) {
      return true;
    }
    return userHasAccessToResource(template, userEmail.value);
  });
});

export const userDatasets = computed(() => {
  return allDatasets.value?.filter((dataset) => {
    if (isAdmin.value) {
      return true;
    }
    return userHasAccessToResource(dataset, userEmail.value);
  });
});

export const listTypeDatasets = computed(() => {
  return allDatasets.value?.filter((dataset) => dataset.datasetType === "List");
});

export const companyDataset = computed(() => {
  return allDatasets.value?.find(
    (dataset) => dataset.id === envVariables.value?.REACT_APP_TEMPLATE_HEADER_COMPANY_ID
  );
});

export const branchDataset = computed(() => {
  return allDatasets.value?.find(
    (dataset) => dataset.id === envVariables.value?.REACT_APP_TEMPLATE_HEADER_BRANCH_ID
  );
});

export const departmentDataset = computed(() => {
  return allDatasets.value?.find(
    (dataset) => dataset.id === envVariables.value?.REACT_APP_TEMPLATE_HEADER_DEPARTMENT_ID
  );
});

export const splitEnabledDataset = computed(() => {
  return allDatasets.value?.find(
    (dataset) => dataset.id === "864a2cd5-64c2-428b-9588-873d61b6c191"
  );
});

export const splitDeleteAndCreateEnabledDataset = computed(() => {
  return allDatasets.value?.find(
    (dataset) => dataset.id === "72df2fdc-dda6-4c1c-8480-8ddabfa17262"
  );
});

//#region Window Visibility Signals
export const expressionBuilderVisibility = signal(false);
export const conditionalFormattingVisibility = signal(false);
export const aiPromptVisibility = signal(false);
export const visibilityBuilderVisibility = signal(false);
export const conditionalRowFormattingVisibility = signal(false);
//#endregion Window Visibility Signals

//#region entry data
const continuationTokens = {};
const DEFAULT_PAGE_SIZE = 100;
const INITIAL_LOAD_SIZE = 300;

export const getEntriesByTemplateId = async (templateId, loadType = "initial") => {
  if (!templateId) return;

  const existingEntries = userEntries.value?.[templateId] || [];

  if (loadType === "initial" && existingEntries.length > 0) {
    return;
  }

  let continuationToken = continuationTokens[templateId] || null;
  let maxItemCount = loadType === "initial" ? INITIAL_LOAD_SIZE : DEFAULT_PAGE_SIZE;

  if ((loadType === "more" || loadType === "all") && continuationToken === null) {
    return;
  }

  do {
    const baseUrl = `${process.env.REACT_APP_API_ENDPOINT}/api/Entry/templateId/${templateId}?maxItemCount=${maxItemCount}`;
    const url = continuationToken
      ? `${baseUrl}&continuationToken=${encodeURIComponent(continuationToken)}`
      : baseUrl;

    const response = await axios.get(url);
    const newEntries = response.data.entries || [];
    existingEntries.push(...newEntries);

    userEntries.value = {
      ...userEntries.value,
      [templateId]: existingEntries,
    };

    entriesLoaded.value = true;

    continuationToken = response.data.continuationToken;
    continuationTokens[templateId] = continuationToken;

    if (loadType === "initial" || loadType === "more") {
      break;
    }
  } while (continuationToken);

  return continuationToken;
};
//#endregion entry data

export const getDatasetById = (id) => {
  return allDatasets.value?.find((dataset) => dataset.id === id);
};

export const newHomepageEnabled = signal(false);
export const darkModeEnabled = signal(false);

//#region User Preferences / Favorite Templates / Favorite Datasets
export const userPreferences = signal(null);

export const favoriteTemplates = computed(() => {
  return userPreferences.value?.favoriteTemplatesIds || [];
});

export const toggleFavoriteTemplate = (templateId) => {
  const currentFavorites = favoriteTemplates.value || [];
  const isFavorite = currentFavorites.includes(templateId);

  let newFavorites;
  if (isFavorite) {
    newFavorites = currentFavorites.filter((id) => id !== templateId);
  } else {
    newFavorites = [...currentFavorites, templateId];
  }

  // Update signal
  userPreferences.value = {
    ...userPreferences.value,
    favoriteTemplatesIds: newFavorites,
  };

  // Sync to backend with debounce
  saveUserPreferencesDebounced(
    userPreferences.value?.id,
    userEmail.value,
    newFavorites,
    favoriteDatasets.value,
    newHomepageEnabled.value,
    darkModeEnabled.value
  );
};

export const isTemplateFavorite = (templateId) => {
  return favoriteTemplates.value.includes(templateId);
};

export const favoriteDatasets = computed(() => {
  return userPreferences.value?.favoriteDatasetsIds || [];
});

export const toggleFavoriteDataset = (datasetId) => {
  const currentFavorites = favoriteDatasets.value || [];
  const isFavorite = currentFavorites.includes(datasetId);

  let newFavorites;
  if (isFavorite) {
    newFavorites = currentFavorites.filter((id) => id !== datasetId);
  } else {
    newFavorites = [...currentFavorites, datasetId];
  }

  // Update signal
  userPreferences.value = {
    ...userPreferences.value,
    favoriteDatasetsIds: newFavorites,
  };

  // Sync to backend with debounce
  saveUserPreferencesDebounced(
    userPreferences.value?.id,
    userEmail.value,
    favoriteTemplates.value,
    newFavorites,
    newHomepageEnabled.value,
    darkModeEnabled.value
  );
};

export const isDatasetFavorite = (datasetId) => {
  return favoriteDatasets.value.includes(datasetId);
};
//#endregion User Preferences / Favorite Templates / Favorite Datasets

//#region Azure Storage Account
export const blobServiceClient = computed(() => {
  if (envVariables.value?.REACT_APP_AZSTG_CONNECTION) {
    const blobServiceClient = new BlobServiceClient(
      envVariables.value.REACT_APP_AZSTG_CONNECTION.toString()
    );

    return blobServiceClient;
  }
});
//#endregion Azure Storage Account

// Send error notification e-mail
effect(() => {
  if (notification.value?.show && notification.value?.type === "error") {
    const url = envVariables.value.REACT_APP_AZ_LA_EMAIL;

    const emailBody = `
      <div style="font-family: Arial, sans-serif; color: #1a1a1a; line-height: 1.5;">
        <h2 style="color: red; margin-bottom: 16px;">Atlas Error Notification</h2>
        
        <p><strong>Message:</strong> ${notification.value?.error?.message}</p>

        <p><strong>Stack Trace:</strong></p>
        <pre style="padding: 10px; border: 1px solid #ccc; white-space: pre-wrap; border-radius: 4px;">
          ${notification.value?.error?.stack}
        </pre>

        <hr style="margin: 20px 0;" />

        <p style="margin-top: 20px;">
          Please investigate the above error.<br />
          This email was sent automatically by the Atlas application.
        </p>
      </div>
    `;

    const userTemplatesSignal = userTemplates.value;
    const userDatasetsSignal = userDatasets.value;
    const userEntriesSignal = userEntries.value;

    const generalSignals = {
      userData: userData.value,
      selectedHamburgerIndex: selectedHamburgerIndex.value,
      template: template.value,
      selectedTemplate: selectedTemplate.value,
      entryValues: entryValues.value,
      formErrors: formErrors.value,
      selectedTab: selectedTab.value,
      selectedStep: selectedStep.value,
      galleryValues: galleryValues.value,
      tableValues: tableValues.value,
    };

    const toBase64 = (obj) => {
      if (!obj) return null;

      try {
        const str = JSON.stringify(obj, null, 2);
        const bytes = new TextEncoder().encode(str);
        const binString = Array.from(bytes, (byte) => String.fromCharCode(byte)).join("");
        return btoa(binString);
      } catch (e) {
        console.warn("Failed to encode object to Base64:", e);
        return null;
      }
    };

    const rawAttachments = [
      {
        Name: "userTemplatesSignal.json",
        ContentBytes: toBase64(userTemplatesSignal),
      },
      {
        Name: "userDatasetsSignal.json",
        ContentBytes: toBase64(userDatasetsSignal),
      },
      {
        Name: "userEntriesSignal.json",
        ContentBytes: toBase64(userEntriesSignal),
      },
      {
        Name: "generalSignals.json",
        ContentBytes: toBase64(generalSignals),
      },
    ];

    const attachments = rawAttachments.filter(
      (att) => att.ContentBytes && att.ContentBytes.trim() !== ""
    );

    const emailProperties = {
      To: "bdf75922.compassminerals0.onmicrosoft.com@amer.teams.ms",
      Subject: "ATLAS ERROR NOTIFICATION",
      Body: emailBody,
      Attachments: attachments,
    };

    axios.post(url, emailProperties).catch((e) => {
      console.error("Error sending email:", e.message);
    });
  }
});
